import {
  Download,
  FileText,
  Image,
  Layers,
  MapPin,
  Spline,
  Square,
  Table2,
} from "lucide-react";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/components/theme-provider";
import { cn } from "@/lib/utils";
import type { CatalogueCardEntry } from "content/catalogue";
import { TruncatedText } from "components/shared/truncated-text";
import dayjs from "dayjs";
import { Link, useHistory } from "react-router-dom";
import * as Routes from "routes";
import { productCodeToExplorerLayerIdMappings } from "../constants";
import { viewDatasetInExplorer } from "./catalogue-page";
import { ComingSoonOverlay } from "./coming-soon";

interface CatalogueCardProps {
  entry: CatalogueCardEntry;
  /** From the datasets API, so absent until that query resolves. */
  updatedDate?: string;
}

export const CatalogueCard = ({ entry, updatedDate }: CatalogueCardProps) => {
  const history = useHistory();
  const { resolvedTheme } = useTheme();
  const image = resolvedTheme === "dark" ? entry.imageDark : entry.imageLight;
  const hasExplorerLayer = !!productCodeToExplorerLayerIdMappings.get(
    entry.datasetProductCode,
  );

  return (
    <Card className="overflow-hidden p-0 gap-0">
      <div className="flex flex-col md:flex-row md:items-stretch flex-1">
        <div className="relative w-full md:w-60 md:min-w-40 shrink-0 overflow-hidden">
          <img
            src={image}
            alt={entry.displayName}
            loading="lazy"
            className={cn(
              "h-full w-full object-cover",
              entry.comingSoon && "scale-110 blur-sm grayscale",
            )}
          />
          {entry.comingSoon ? (
            <ComingSoonOverlay />
          ) : (
            <Badge className="absolute top-2 left-2 gap-1.5">
              {getFeatureIcon(entry.metadata.dataType[0])}
              {Intl.NumberFormat("en", { notation: "compact" }).format(
                entry.metadata.totalFeatureCount,
              )}{" "}
              {humanizeFeatures(entry.metadata.dataType[0])}
            </Badge>
          )}
        </div>

        <div className="flex flex-col flex-1 min-w-0 py-4 gap-4">
          <CardHeader>
            <CardTitle className="min-w-0">
              <TruncatedText className="max-w-full">
                {entry.displayName}
              </TruncatedText>
            </CardTitle>
            {updatedDate && (
              <CardDescription>
                Updated: {dayjs(updatedDate).format("MMM YYYY")}
              </CardDescription>
            )}
            <CardAction>
              <div className="flex gap-1">
                {entry.metadata.dataType.map((type) => (
                  <Badge key={type} variant="outline">
                    {type}
                  </Badge>
                ))}
              </div>
            </CardAction>
          </CardHeader>

          <CardContent>
            <p className="line-clamp-3 text-sm text-muted-foreground">
              {entry.shortDescription}
            </p>
          </CardContent>

          <CardFooter className="flex gap-2 mt-auto">
            <Button className="flex-1" asChild>
              <Link to={`${Routes.dataCatalogue}/${entry.slug}`}>
                <FileText />
                Details
              </Link>
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                history.push(
                  `${Routes.dataDownload}?product=${entry.datasetProductCode}`,
                )
              }
            >
              <Download />
              Downloads
            </Button>
            <Button
              variant="outline"
              disabled={!hasExplorerLayer}
              onClick={() =>
                viewDatasetInExplorer(entry.datasetProductCode, history)
              }
            >
              <Layers />
              Explorer
            </Button>
          </CardFooter>
        </div>
      </div>
    </Card>
  );
};

const getFeatureIcon = (dataType: string) => {
  const className = "size-3";
  switch (dataType) {
    case "Point":
      return <MapPin className={className} />;
    case "Polygon":
    case "Multi Polygon":
      return <Square className={className} />;
    case "Line":
    case "Multi Line":
      return <Spline className={className} />;
    case "Raster":
      return <Image className={className} />;
    case "Table":
      return <Table2 className={className} />;
    default:
      return <MapPin className={className} />;
  }
};

const humanizeFeatures = (dataType: string) => {
  switch (dataType) {
    case "Raster":
      return "Sq KM";
    case "Table":
      return "Rows";
    default:
      return "Features";
  }
};
